import React, { useState, useEffect, useContext, useRef } from 'react';
import { format as d3format } from 'd3-format';
import classNames from 'classnames/bind';
import download from 'core/utils/save-string-as-file';
import SessionContext from 'SessionContext';
import ProgressLoader from 'partials/progress-loader';
import { FormGroup, Input, Label, Button, Modal, ModalHeader, ModalBody, ModalFooter, InputGroup } from 'reactstrap';
import RadiusSelector from './chart-base-radius';
import { getBrandColor } from './brand-color-map';

export const nonClassNameSafe = /[^a-zA-Z0-9-]/g;
const formatNumber = d3format(',d');
export { formatNumber };

function humanizeNumber(num, fullNumber) {
  if (fullNumber) {
    return formatNumber(num);
  } else {
    if (num < 100000) return formatNumber(num);
    num = Math.floor(num / 1000);
    if (num < 1000) return num + 'k';
    num = (num / 1000).toFixed(2);
    return num + 'M';
  }
}
export { humanizeNumber };

export function formatClassName(str) {
  return str.replace(nonClassNameSafe, '').toLowerCase();
}

export function formatTitle(d) {
  return `${d[1]}: ${formatNumber(d[2])}`;
}

export function formatPercentageText(s) {
  return Math.round(100 * s.data.percentage);
}

export function extractIcon(svg) {
  const frag = document.createRange().createContextualFragment(svg);
  const icon = frag.querySelector('svg');
  return icon;
}


// Custom hook for chart base logic
export function useChartBase(props) {
  const context = useContext(SessionContext);
  const [scrolledToTop, setScrolledToTop] = useState(true);
  const [scrolledToBottom, setScrolledToBottom] = useState(true);
  const [expanded, setExpanded] = useState(props.expandedView || false);
  const [newload, setNewload] = useState(false);
  const [showFlytoNotFound, setShowFlytoNotFound] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [specialCaseLabels] = useState({});
  const [updatedLabelList, setUpdatedLabelList] = useState(null);
  const [excluded, setExcluded] = useState(props.config.excluded || false);
  const chartEL = useRef(null);

  useEffect(() => {
    measureScrollHeight();
    setNewload(true);
  }, []);

  useEffect(() => {
    if (newload || (props.filters && props.filters !== undefined)) {
      if (!props.skipAutoFilterUpdate) {
        props.onChartUpdate && props.onChartUpdate(true, newload, props.config?.id);
      }
      measureScrollHeight();
      setNewload(false);
    }
    // eslint-disable-next-line
  }, [props.filters, newload]);

  function getCurrentDate() {
    const date = new Date();
    let day = date.getDate();
    let month = date.getMonth() + 1;
    let year = date.getFullYear();
    let currentDate = `${month}-${day}-${year}`;
    return currentDate;
  }

  async function triggerDownload(downloadFn) {
    let audienceName = context.crossfilterStore.audience.name;
    audienceName = (audienceName) ? audienceName.replace(/[^A-z0-9-_]+/g, "_") + ' - ' : 'Untitled - ';
    const filename = `${audienceName}${props.config.label} - ${getCurrentDate()}.csv`;
    let headers;
    const hasMatchLevels = props.config.uiType === "counts-match";
    headers = context.crossfilterStore.getCSVHeaders(hasMatchLevels);
    const values = await getDownloadValues(getUnits(headers));
    downloadFn(filename, headers, values);
  }

  async function getDownloadValues(units) {
    const id = [props.config.id];
    const type = (props.viewOnly) ? "Insight" : "Filter";
    const csvFilters = await context.crossfilterStore.getAudienceDownloadData(id, type);
    return csvFilters.flat();
  }

  function getUnits(headers) {
    let units = "";
    let re = /\(in.*\)/i;
    if (headers && headers[0] && headers[0].match(re)) {
      units = headers[0].match(re)[0];
      units = units.replace(/\(in/i, "");
      units = units.replace(/\)/, "");
    }
    return units;
  }

  function getClassName() { return ''; }

  function renderHover() {
    let hover = '';
    if (props.config.excluded) {
      hover = props.config.excluded;
    }
    if (props.config.long_txt && props.config.long_txt.trim() !== "") {
      hover = hover + props.config.long_txt;
    } else if (props.config.label) {
      hover = hover + props.config.label;
    }
    return hover;
  }

  function renderTitle() {
    let title = '';
    if (props.config.excluded) {
      title = props.config.excluded;
    }
    if (props.config.label) {
      title = title + props.config.label;
    }
    if (props.config.uiType && props.config.uiType === "location_type_counts" && !props.viewOnly) {
      let shortTitle = title.split(':');
      let hasDropdown = <>{props.config.excluded ? `${shortTitle[0]}: ${shortTitle[1]}` : shortTitle[0]}{" "}</>;
      title = hasDropdown;
    }
    return title;
  }

  // Leave for LocationTypeWidget Functional Refactor
  function signalRender() {}

  function renderDropdown(config) {
    if (props.config.uiType && props.config.uiType === "location_type_counts") {
      return (<RadiusSelector config={config} signalRender={signalRender} />);
    }
    return null;
  }

  function renderSummary() {
    const config = props.config,
    filters = props.filters || [];
    return (
      <ul>
        {filters.map((filter, i) =>
          <li key={i}>{config.value_labels[filter.value] || filter.value}
            {filter.match_value && <span className='match-level-details'><span className='label'>Match Level:</span><span className='value'>{filter.match_value}</span></span>}
          </li>
        )}
      </ul>
    );
  }

  function onClearFilters(ev) {
    ev && ev.preventDefault();
    context.crossfilterStore.set_filters(props.config.id, []);
    props.onChartUpdate && props.onChartUpdate();
  }

  function onClickDownload() {
    triggerDownload((filename, headers, values) => {
      const content = [headers.join(',')].concat(values.map(v => v.join(','))).join('\n');
      download(filename, content);
    });
  }

  function measureScrollHeight(el) {
    el = el || chartEL.current;
    if (!el) return;
    setScrolledToTop(el.scrollTop === 0);
    setScrolledToBottom(el.scrollTop > el.scrollHeight - el.offsetHeight - 2);
  }

  function onChartScroll(ev) {
    measureScrollHeight(ev.target);
  }

  function toggleExpand(ev) {
    ev.stopPropagation();
    ev.preventDefault();
    setExpanded(!expanded);
    setTimeout(() => measureScrollHeight(), 50);
  }

  function sortValues(values){
    return values;
  }

  function sortListOutput(labels, values, filters, sortOrder = 'asc', sortField = 'label') {
    let prefix = context.crossfilterStore.geoPrefix;
    let is_state = `${prefix}state`;

    if(!filters.length && (config.id === is_state)) {
      const filterWidget = context.crossfilterStore?.filters[is_state];
      filters = filterWidget;
    }
    
    // build new object from data
    const mergeObj = [];
    // normal filtering with all values
    if (Object.keys(labels).length) {
      for (const key in labels) {
        const filtered = (filters?.find(filter => filter.value === key)) ? true : false;
        const _filter = filters?.find(filter => filter.value === key);
        mergeObj.push({ id: key, label: labels[key], value: values[key], filtered: filtered, match_level: _filter?.match_value || null });
      }
      // if no labels or values it's the states. If this is not done the selected states will not show when filtered down to 0 by another filter
      // this adds the filtered states to the widget. 
    } else {
      for (const key in filters) {
        mergeObj.push({ id: filters[key].value, label: filters[key].value, value: 0, filtered: true });
      }
    }

    // sort everything but the person type widget which is based upon count-widget
    if (props.config.id !== 'persontype') {
      mergeObj.sort((a, b) => {
        return b.filtered - a.filtered;
      });  
    }

    return mergeObj;
  }

  function searchWidgetValues(searchStr, config, values) {
    if (!searchStr.length) {
      setUpdatedLabelList(null);
      return;
    }
    let labels = config.value_labels || {};
    if (!labels || Object.keys(labels).length === 0) {
      labels = specialCaseLabels;
    }
    function filterObjectByValue(obj, targetString) {
      if (typeof obj !== 'object' || obj === null) {
        return {};
      }
      let result = Array.isArray(obj) ? [] : {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const value = obj[key].toLowerCase();
          if (typeof value === 'string' && value.includes(targetString)) {
            result[key] = value;
          } else if (typeof value === 'object' && value !== null) {
            const filteredValue = filterObjectByValue(value, targetString);
            if (Array.isArray(result)) {
              result.push(filteredValue);
            } else if (Object.keys(filteredValue).length > 0 || Array.isArray(value)) {
              result[key] = filteredValue;
            }
          }
        }
      }
      return result;
    }
    // eslint-disable-next-line
    let updatedList = filterObjectByValue(labels, searchStr);
    setUpdatedLabelList(updatedList);
  }

  function onClickRemove(config) {
    let values = [];
    function getAllNames(rows) {
      let getNames = [];
      rows.forEach((row) => {
        let label = config.label.split(':')[0].trim();
        if (label === row.location_type__name) {
          getNames.push(row);
        }
      });
      return getNames;
    }
    // Use cached mapList from crossfilterStore instead of fetching
    if (config.uiType === "location_type_counts") {
      if (context.crossfilterStore.mapListData) {
        values = getAllNames(context.crossfilterStore.mapListData);
        context.crossfilterStore.remove_location_type_icon(values);
      }
      props.onRemove(config);
    } else {
      props.onRemove(config);
    }
    if(config.uiType === "geo-boundary" || config.uiType === "locations") {
      if(!filters.length && excluded){
        onFilterNegate(false);
      }
    }
  }

  function onFilterNegate(is_negated, ev) {
    if (props.viewOnly || is_negated === undefined) return;
    if (ev && ev.stopPropagation) ev.stopPropagation();

    // Create new filter objects with updated is_negated (don't mutate existing)
    // Use set_filters for atomic bulk update instead of multiple add_filter calls
    const updatedFilters = props.filters.map((filter) => ({
      ...filter,
      is_negated: is_negated
    }));

    context.crossfilterStore.set_filters(props.config.id, updatedFilters);
    setExcluded(is_negated ? "excluded: " : "");
    props.onChartUpdate && props.onChartUpdate();
  }

  function onFilterClick(add, chart, series, val, is_negated, ev) {
    if (props.viewOnly) return;
    if (ev && ev.stopPropagation) ev.stopPropagation();
    const toggleFilterFn = add ? context.crossfilterStore.add_filter : context.crossfilterStore.remove_filter; 
    toggleFilterFn(chart.id, series, val, is_negated);
    props.onChartUpdate && props.onChartUpdate(add);
  }

  let {
    config,
    tween,
    className: classNameProp,
    filters,
    summarize,
    values,
    onRemove,
    onChartUpdate,
    viewOnly,
    allowDownload,
    show_legend,
    draggable,
    onDragStart,
    onDragEnd,
    // eslint-disable-next-line
    ...rest
  } = props;

  const access = context.appState.userCan;
  filters = filters || [];
  const isFiltered = !!filters.length;
  const isLoading = context.crossfilterStore.loading_chart_data;
  const isDraggable = !!draggable && !searchText;
  const removable = !!onRemove;
  const downloadable = allowDownload && !summarize && triggerDownload;
  const style = config.color ? { '--color': getBrandColor(config.color) } : {};
  let class_names = classNames(
    "chart-widget",
    classNameProp,
    getClassName(),
    config.label ? formatClassName(config.label) + "-chart" : "",
    { "is-filtered": isFiltered && !viewOnly, "is-loading": isLoading }
  );

  const handleSearch = (ev) => {
    ev.preventDefault();
    let searchStr = ev.target.value.toLowerCase().trimStart();
    setSearchText(searchStr);
    searchWidgetValues(searchStr, config, values);
  };

  let listIsLong = config.value_labels ? (Object.keys(config.value_labels).length > 10) : false;
  if (!listIsLong && config.label === "Home Market Value") {
    listIsLong = true;
  }
  const widgetsWithSearch = ["bar", "counts", "counts-match", "location_type_counts"];
  let ui_type = config.uiType.toString().trim();
  let showSearch = false;
  if (!viewOnly) {
    if (widgetsWithSearch.includes(ui_type)) {
      showSearch = true;
    } else {
      showSearch = false;
    }
  }

  function flyTo_builder(lon, lat, zoom = 7) {
    let flyTo_defaults = {zoom: 9};
    if(lon && lat) {
      props.map.flyTo({ ...flyTo_defaults, ...{center: [lon, lat], zoom: zoom}, essential: true });
    } else {
      setShowFlytoNotFound(true);
    }
  };

  function handleFlyToZipcode(zip){
    context.query("location/get_latitude_longitude_for_zipcode", { zipcode: zip })
      .then((res) => {
        flyTo_builder(res.longitude, res.latitude, 9);
      })
      .catch(() => console.log("Failed to get coordinates."));
  }

  function handleFlyToCounty(county){
    context.query("location/get_latitude_longitude_for_county", { geoid: county })
      .then((res) => {
        flyTo_builder(res.longitude, res.latitude, 8);
      })
      .catch(() => console.log("Failed to get coordinates."));
  }

  function handleFlyToCongress(congress) {
    context.query("location/get_latitude_longitude_for_congressional_district", { geoid: congress })
      .then((res) => {
        flyTo_builder(res.longitude, res.latitude, 6);
      })
      .catch(() => console.log("Failed to get coordinates."));
  }

  function handleFlyToCBSA(cbsa) {
    context.query("location/get_latitude_longitude_for_cbsa", { geoid: cbsa })
      .then((res) => {
        flyTo_builder(res.longitude, res.latitude, 6);
      })
      .catch(() => console.log("Failed to get coordinates."));
  }

  function handleFlyToState(st) {
    context.query("location/get_latitude_longitude_for_state", { state: st })
      .then((res) => {
        flyTo_builder(res.longitude, res.latitude, 6);
      })
      .catch(() => console.log("Failed to get coordinates."));
   }

  // Compute selected (filtered) ids for convenience, similar to ChartBase
  let selected = [];
  if (filters && Array.isArray(filters)) {
    selected = filters.map(f => f.value);
  }

  return {
    access,
    filters,
    isFiltered,
    isLoading,
    isDraggable,
    removable,
    downloadable,
    style,
    class_names,
    excluded,
    handleSearch,
    listIsLong,
    showSearch,
    renderHover,
    renderTitle,
    renderDropdown,
    renderSummary,
    onClearFilters,
    onClickDownload,
    onClickRemove,
    onFilterNegate,
    onFilterClick,
    scrolledToTop,
    scrolledToBottom,
    expanded,
    chartEL,
    onChartScroll,
    toggleExpand,
    summarize,
    searchText,
    updatedLabelList,
    setSearchText,
    showFlytoNotFound,
    setShowFlytoNotFound,
    selected,
    sortListOutput,
    sortValues,
    handleFlyToZipcode,
    handleFlyToCounty,
    handleFlyToCongress,
    handleFlyToCBSA,
    handleFlyToState,
    props,
  };
}

// Presentational component using the hook
const ChartBase = (props) => {
  const base = props.base;

  const chartSummaryRenderer = props.renderSummary
    ? () => props.renderSummary({ ...props, base })
    : () => base.renderSummary(props);

  useEffect(() => {
    let check_if_excluded = props.filters[0]?.is_negated;

    if(check_if_excluded === undefined) { return }

    if(base.excluded !== check_if_excluded) {
      base.onFilterNegate(check_if_excluded);
    }

  // eslint-disable-next-line
  }, [base.filters, props.config])

  return (
    <figure
      className={`${base.class_names} ${props.chartClassName || ''}`}
      style={base.style}
      key={props.chartKey}
    >
      <figcaption className='heading' draggable={base.isDraggable} onDragStart={props.onDragStart} onDragEnd={props.onDragEnd}>
        <span>
          <span className='hovertext' data-hover={base.renderHover()}>{base.renderTitle()}</span>
          {!props.viewOnly && (<span className="d-block mt-1 mb-1">
            {base.renderDropdown(props.config)}
          </span>)}
          {base.listIsLong && base.showSearch && (
            <InputGroup className="widget-search">
              <Input
                type="search"
                placeholder="Search"
                onChange={base.handleSearch}
                value={base.searchText || ""}
                className="widget-search-input"
              />
            </InputGroup>
          )}
        </span>
        <i className='spacer' />
        <div className='actions'>
          {base.downloadable && <button className='fa-solid fa-download' title='Download as CSV'
            onClick={base.onClickDownload}></button>}
          {base.isFiltered && !props.viewOnly && <button title='Clear Filters' onClick={base.onClearFilters}
            className='clear-filters'>
            <span className='count'>{base.filters.length}</span></button>}
          {(base.removable && (!base.isFiltered || props.viewOnly)) && base.access('audience_edit') && <button className="fa fa-xmark remove-chart" title="Remove Widget"
            onClick={() => base.onClickRemove(props.config)} />}
        </div>
      </figcaption>
      {!props.viewOnly &&
        // props.config.uiType !== "locations" &&
        <div className="exclusion">
          <FormGroup switch>
            <Input
              disabled={!base.isFiltered}
              type="switch"
              checked={base.excluded}
              onClick={(e) => {
                base.onFilterNegate((base.excluded) ? 0 : 1, e)
              }}
              onChange={() => { }}
            />
            <Label check>Exclude selected filters</Label>
          </FormGroup>
        </div>}
      {base.isLoading && <ProgressLoader />}
      {base.summarize &&
        <div className='summary'>
          {!base.isFiltered && <p>No Filters</p>}
          {base.isFiltered && chartSummaryRenderer()}
        </div>
      }
      {!base.summarize &&
        <div className={classNames('chart-scrollwrap', {
          'overflow-top': !base.scrolledToTop,
          'overflow-bottom': !base.scrolledToBottom,
          'expanded': base.expanded
        })}>
          <div className={classNames('chart')}
            onScroll={base.onChartScroll} ref={base.chartEL}>
            {props.children}
          </div>
          <div className='before'><i className='spacer' /><span>more</span></div>
          <div className='after' onClick={base.toggleExpand}>
            <i className='spacer' />
            <span className='more'>more</span>
            <span className='show-all'>expand</span>
          </div>
          <div className='show-less' onClick={base.toggleExpand}>
            <i className='spacer' />
            <span>collapse</span>
          </div>
        </div>
      }
      {props.addWidgetsToRails && props.addWidgetsToRails(props.config.id)}
      {
        <Modal centered backdrop isOpen={base.showFlytoNotFound}>
          <ModalHeader>
            Location Not Found
          </ModalHeader>
          <ModalBody>
            We could not find that location on the map.
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => { base.setShowFlytoNotFound(false); }}>Close</Button>
          </ModalFooter>
        </Modal>
      }
    </figure>
  );
};

export default ChartBase;
